import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  CreditCardIcon, ArrowPathIcon, BanknotesIcon, ReceiptPercentIcon, UserGroupIcon, CheckIcon,
  ArrowUpRightIcon, ArrowDownLeftIcon, ExclamationTriangleIcon, LockClosedIcon, PlusIcon,
} from '@heroicons/react/24/outline'
import {
  getWallet, getTransactions, createTopup, updateApplicantFee, getPayments, billingErrorMessage,
  type Wallet, type BillingTransaction, type BillingPayment, type TransactionType,
} from '../../lib/billingApi'
import { formatMoney, formatMonthLabel, parseDollarsToCents, centsToInputValue } from '../../lib/money'
import { readPaymentReturn, readPaymentReason, paymentFailureText, topupPresets, validateTopup, verificationsCovered, stripPaymentReturnParams } from '../../lib/billing'
import { Card, CardHeader, PillButton, EmptyState, Segmented, Skeleton, StatusTag, Toggle, Modal, cx } from '../dashboard/primitives'
import { FolderStatCard } from '../dashboard/FolderStatCard'
import { PageHead, HeaderStats } from '../dashboard/Layout'
import { BarChart, Legend } from '../dashboard/charts'

export const TX_TYPE_LABEL: Record<TransactionType, string> = {
  topup: 'Top-up',
  verification: 'Verification',
  applicant_fee: 'Applicant fee',
  refund: 'Refund',
  adjustment: 'Adjustment',
}
const TX_FILTERS: Array<{ value: '' | TransactionType; label: string }> = [
  { value: '', label: 'All' },
  { value: 'topup', label: 'Top-ups' },
  { value: 'verification', label: 'Verifications' },
  { value: 'applicant_fee', label: 'Applicant fees' },
  { value: 'refund', label: 'Refunds' },
  { value: 'adjustment', label: 'Adjustments' },
]
const PAGE = 10

export const fmtDateTime = (iso: string) => {
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}, ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
}

export function TxIcon({ type, amount }: { type: TransactionType; amount: number }) {
  const icon = type === 'topup' ? <BanknotesIcon /> : type === 'applicant_fee' ? <UserGroupIcon /> : type === 'verification' ? <ReceiptPercentIcon /> : amount >= 0 ? <ArrowDownLeftIcon /> : <ArrowUpRightIcon />
  return <span className={cx('sf-roundicon', amount >= 0 ? 'accent' : '')} style={{ width: 34, height: 34 }}>{icon}</span>
}

export function Pager({ offset, total, limit, onChange }: { offset: number; total: number; limit: number; onChange: (o: number) => void }) {
  if (total <= limit) return total > 0 ? <div className="sf-pager"><span>{total.toLocaleString()} item{total === 1 ? '' : 's'}</span></div> : null
  const page = Math.floor(offset / limit) + 1, pages = Math.ceil(total / limit)
  return (
    <div className="sf-pager">
      <span>Showing {offset + 1}–{Math.min(offset + limit, total)} of {total.toLocaleString()}</span>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <PillButton size="sm" variant="grey" disabled={page <= 1} onClick={() => onChange(Math.max(0, offset - limit))}>Previous</PillButton>
        <span className="sf-num">{page} / {pages}</span>
        <PillButton size="sm" variant="grey" disabled={page >= pages} onClick={() => onChange(offset + limit)}>Next</PillButton>
      </div>
    </div>
  )
}

// ─── Top-up modal ────────────────────────────────────────────────────

function TopupModal({ wallet, onClose }: { wallet: Wallet; onClose: () => void }) {
  const presets = topupPresets(wallet.min_topup_cents)
  const [selected, setSelected] = useState<number | 'custom'>(presets[1] ?? presets[0])
  const [custom, setCustom] = useState('')
  const [busy, setBusy] = useState(false)
  const amount = selected === 'custom' ? parseDollarsToCents(custom) : selected
  const error = selected === 'custom' && custom ? validateTopup(amount, wallet.min_topup_cents) : null
  const covered = amount ? verificationsCovered(amount, wallet.price_per_verification_cents) : null

  const submit = async () => {
    const err = validateTopup(amount, wallet.min_topup_cents)
    if (err || amount === null) { toast.error(err ?? 'Enter an amount'); return }
    setBusy(true)
    try {
      const { checkout_url } = await createTopup(amount)
      window.location.assign(checkout_url)
    } catch (e) {
      toast.error(billingErrorMessage(e, 'Could not start checkout'))
      setBusy(false)
    }
  }

  return (
    <Modal title="Top up wallet" sub={`Prepaid USD balance · minimum ${formatMoney(wallet.min_topup_cents, { trimWhole: true })}`} onClose={onClose}
      footer={<>
        <PillButton variant="grey" onClick={onClose} disabled={busy}>Cancel</PillButton>
        <PillButton variant="primary" onClick={submit} disabled={busy || !amount || !!error} icon={<LockClosedIcon />}>
          {busy ? 'Redirecting…' : `Pay ${amount ? formatMoney(amount) : ''}`}
        </PillButton>
      </>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 }}>
        {presets.map(p => (
          <button key={p} type="button" onClick={() => setSelected(p)}
            className={cx('sf-pill', selected === p ? 'dark' : 'grey')} style={{ height: 54, borderRadius: 18, fontSize: 17, fontWeight: 600 }}>
            {formatMoney(p, { trimWhole: true })}
          </button>
        ))}
        <button type="button" onClick={() => setSelected('custom')} className={cx('sf-pill', selected === 'custom' ? 'dark' : 'grey')} style={{ height: 54, borderRadius: 18 }}>Custom</button>
      </div>
      {selected === 'custom' && (
        <div style={{ marginTop: 16 }}>
          <label className="sf-label" htmlFor="topup-custom">Amount (USD)</label>
          <div className="sf-input-wrap">
            <span className="sf-prefix">$</span>
            <input id="topup-custom" className={cx('sf-input', error && 'err')} inputMode="decimal" autoFocus placeholder={centsToInputValue(wallet.min_topup_cents)} value={custom} onChange={e => setCustom(e.target.value)} />
          </div>
          {error && <div className="sf-field-err">{error}</div>}
        </div>
      )}
      <div className="sf-card muted" style={{ marginTop: 16, padding: 16, borderRadius: 20, display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13.5 }}>
        <span style={{ color: 'var(--sf-mid)' }}>At {formatMoney(wallet.price_per_verification_cents)} per verification this covers</span>
        <b className="sf-num">{covered === null ? '—' : `${covered.toLocaleString()} checks`}</b>
      </div>
      <p className="sf-help" style={{ marginTop: 12 }}>You'll be redirected to Flutterwave's secure checkout (card or mobile money) and brought back here afterwards.</p>
    </Modal>
  )
}

// ─── Applicant fee settings ──────────────────────────────────────────

function ApplicantFeeCard({ wallet, onSaved }: { wallet: Wallet; onSaved: (w: Wallet) => void }) {
  const [enabled, setEnabled] = useState(wallet.applicant_fee.enabled)
  const [amount, setAmount] = useState(centsToInputValue(wallet.applicant_fee.amount_cents || 500))
  const [description, setDescription] = useState(wallet.applicant_fee.description || '')
  const [busy, setBusy] = useState(false)
  const cents = parseDollarsToCents(amount)
  const amountErr = cents === null ? 'Enter an amount in dollars' : cents < 50 ? 'Minimum fee is $0.50' : cents > 100000 ? 'Maximum fee is $1,000.00' : null
  const dirty = enabled !== wallet.applicant_fee.enabled || cents !== wallet.applicant_fee.amount_cents || description !== (wallet.applicant_fee.description || '')

  const save = async () => {
    if (enabled && amountErr) { toast.error(amountErr); return }
    setBusy(true)
    try {
      const fee = await updateApplicantFee({ enabled, amount_cents: cents ?? wallet.applicant_fee.amount_cents, description: description.trim() })
      // Server returns description null when empty
      fee.description = fee.description ?? ''
      onSaved({ ...wallet, applicant_fee: fee })
      toast.success(fee.enabled ? 'Applicant fee is on' : 'Applicant fee is off')
    } catch (e) {
      toast.error(billingErrorMessage(e, 'Could not save the applicant fee'))
    } finally { setBusy(false) }
  }

  return (
    <Card>
      <CardHeader title="Applicant fee" sub="Charge each person a fee before their verification starts" />
      <Toggle on={enabled} onChange={setEnabled} label={enabled ? 'Applicants pay before verifying' : 'Off — you pay for every verification'} sub="Paid fees are credited to your wallet" />
      <div className="sf-form-row" style={{ marginTop: 18 }}>
        <div>
          <label className="sf-label" htmlFor="fee-amount">Fee per applicant (USD)</label>
          <div className="sf-input-wrap">
            <span className="sf-prefix">$</span>
            <input id="fee-amount" className={cx('sf-input', enabled && amountErr && 'err')} inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} disabled={!enabled} />
          </div>
          {enabled && amountErr ? <div className="sf-field-err">{amountErr}</div> : <div className="sf-help">Between $0.50 and $1,000</div>}
        </div>
        <div>
          <label className="sf-label" htmlFor="fee-desc">Description shown to applicants</label>
          <input id="fee-desc" className="sf-input" maxLength={255} placeholder="Identity verification fee" value={description} onChange={e => setDescription(e.target.value)} disabled={!enabled} />
          <div className="sf-help">{description.length}/255</div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <PillButton variant="dark" onClick={save} disabled={busy || !dirty} icon={<CheckIcon />}>{busy ? 'Saving…' : 'Save fee settings'}</PillButton>
      </div>
    </Card>
  )
}

// ─── Main section ────────────────────────────────────────────────────

export function BillingSection({ wallet: initialWallet, onWalletChange, isOperator }: { wallet: Wallet | null; onWalletChange?: (w: Wallet) => void; isOperator?: boolean }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [wallet, setWallet] = useState<Wallet | null>(initialWallet)
  const [walletError, setWalletError] = useState<string | null>(null)
  const [loading, setLoading] = useState(!initialWallet)
  const [txType, setTxType] = useState<'' | TransactionType>('')
  const [txOffset, setTxOffset] = useState(0)
  const [tx, setTx] = useState<{ items: BillingTransaction[]; total: number } | null>(null)
  const [payOffset, setPayOffset] = useState(0)
  const [payments, setPayments] = useState<{ items: BillingPayment[]; total: number } | null>(null)
  const [showTopup, setShowTopup] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const loadWallet = useCallback(async () => {
    try {
      const w = await getWallet()
      setWallet(w); setWalletError(null); onWalletChange?.(w)
    } catch (e) {
      setWalletError(billingErrorMessage(e, 'Billing is unavailable right now'))
    } finally { setLoading(false) }
  }, [onWalletChange])

  useEffect(() => { loadWallet() }, [loadWallet, refreshKey])

  // Return from Flutterwave: ?payment=success|failed&tx_ref=...[&reason=...]
  const [pendingTopup, setPendingTopup] = useState(false)
  useEffect(() => {
    const ret = readPaymentReturn(location.search)
    if (!ret) return
    if (ret === 'success') toast.success('Payment received — your balance has been updated.', { id: 'billing-return' })
    else if (ret === 'pending') {
      toast('Payment is still being confirmed. Your balance will update shortly.', { id: 'billing-return', icon: '⏳' })
      setPendingTopup(true)
    } else toast.error(paymentFailureText(readPaymentReason(location.search)), { id: 'billing-return' })
    setRefreshKey(k => k + 1)
    navigate({ pathname: location.pathname, search: stripPaymentReturnParams(location.search) }, { replace: true })
  }, [location.search, location.pathname, navigate])

  // reason=status_pending: the webhook may still complete the payment — re-poll for ~1 minute.
  useEffect(() => {
    if (!pendingTopup) return
    let n = 0
    const t = setInterval(() => {
      n += 1
      setRefreshKey(k => k + 1)
      if (n >= 6) { clearInterval(t); setPendingTopup(false) }
    }, 10_000)
    return () => clearInterval(t)
  }, [pendingTopup])

  useEffect(() => {
    if (!wallet?.enabled) return
    let cancelled = false
    getTransactions({ limit: PAGE, offset: txOffset, type: txType })
      .then(r => { if (!cancelled) setTx(r) })
      .catch(() => { if (!cancelled) setTx({ items: [], total: 0 }) })
    return () => { cancelled = true }
  }, [wallet?.enabled, txOffset, txType, refreshKey])

  useEffect(() => {
    if (!wallet?.enabled) return
    let cancelled = false
    getPayments({ limit: PAGE, offset: payOffset, payer_type: 'applicant' })
      .then(r => { if (!cancelled) setPayments(r) })
      .catch(() => { if (!cancelled) setPayments({ items: [], total: 0 }) })
    return () => { cancelled = true }
  }, [wallet?.enabled, payOffset, refreshKey])

  const monthly = wallet?.monthly ?? []
  const hasMonthly = monthly.some(m => m.spent_cents > 0 || m.topup_cents > 0 || m.applicant_fee_cents > 0)
  const covered = wallet ? verificationsCovered(wallet.balance_cents, wallet.price_per_verification_cents) : null
  const head = (
    <PageHead
      title="Billing"
      sub="Prepaid USD wallet — each live verification is debited from your balance. Sandbox keys are always free."
      actions={wallet?.enabled ? (<>
        <HeaderStats items={[
          { label: 'Balance', value: formatMoney(wallet.balance_cents, { compact: true }) },
          { label: 'Per check', value: formatMoney(wallet.price_per_verification_cents) },
          { label: 'Checks left', value: covered === null ? '—' : covered.toLocaleString() },
        ]} />
        {!isOperator && <PillButton variant="primary" icon={<PlusIcon />} onClick={() => setShowTopup(true)}>Top up</PillButton>}
      </>) : undefined}
    />
  )

  if (loading) {
    return <div>{head}<div className="sf-grid c4">{[0, 1, 2, 3].map(i => <Skeleton key={i} h={200} r={28} />)}</div></div>
  }
  if (walletError && !wallet) {
    return (
      <div>{head}
        <Card><EmptyState icon={<ExclamationTriangleIcon />} title="Billing couldn't be loaded" action={<PillButton variant="dark" size="sm" icon={<ArrowPathIcon />} onClick={() => { setLoading(true); setRefreshKey(k => k + 1) }}>Try again</PillButton>}>{walletError}</EmptyState></Card>
      </div>
    )
  }
  if (!wallet || !wallet.enabled) {
    return (
      <div>{head}
        <Card style={{ padding: 40 }}>
          <EmptyState icon={<CreditCardIcon />} title="Billing is not enabled on this server"
            action={wallet && !isOperator ? <PillButton variant="dark" size="sm" icon={<PlusIcon />} onClick={() => setShowTopup(true)}>Add prepaid credit</PillButton> : undefined}>
            Verifications are not charged here right now. When the platform operator turns billing on
            (<code>BILLING_ENABLED=true</code>), live verifications are debited from your wallet
            {wallet ? <> — current balance <b>{formatMoney(wallet.balance_cents)}</b>. You can still add credit in advance.</> : '.'}
          </EmptyState>
        </Card>
        {showTopup && wallet && <TopupModal wallet={wallet} onClose={() => setShowTopup(false)} />}
      </div>
    )
  }

  return (
    <div className="sf-fade">
      {head}

      {pendingTopup && (
        <div className="sf-banner soft">
          <span className="sf-roundicon accent"><ArrowPathIcon /></span>
          <div className="sf-banner-body">
            <b>Payment pending confirmation</b>
            <span>Mobile money and some cards confirm a little later. We're checking again every few seconds — refresh shortly if it doesn't appear.</span>
          </div>
        </div>
      )}

      {wallet.low_balance && (
        <div className="sf-banner">
          <span className="sf-roundicon"><ExclamationTriangleIcon /></span>
          <div className="sf-banner-body">
            <b>Low balance — {formatMoney(wallet.balance_cents)} left</b>
            <span>New live verifications are refused (HTTP 402) once your balance can't cover {formatMoney(wallet.price_per_verification_cents)}.</span>
          </div>
          {!isOperator && <PillButton variant="primary" size="sm" onClick={() => setShowTopup(true)}>Top up now</PillButton>}
        </div>
      )}

      <div className="sf-grid c4">
        <FolderStatCard label="Balance" tone="accent" value={formatMoney(wallet.balance_cents, { compact: true })} caption="Covers" captionValue={covered === null ? '—' : `${covered.toLocaleString()} verifications`} />
        <FolderStatCard label="Spent this month" value={formatMoney(wallet.stats.spent_this_month_cents, { compact: true })} caption="Top-ups this month" captionValue={formatMoney(wallet.stats.topups_this_month_cents)} />
        <FolderStatCard label="Verifications" value={wallet.stats.verifications_this_month.toLocaleString()} unit="this month" caption="Price per check" captionValue={formatMoney(wallet.price_per_verification_cents)} />
        <FolderStatCard label="Applicant fees" value={formatMoney(wallet.stats.applicant_fees_this_month_cents, { compact: true })} caption="Status" captionValue={wallet.applicant_fee.enabled ? `On · ${formatMoney(wallet.applicant_fee.amount_cents)} each` : 'Off'} />
      </div>

      <div className="sf-grid main sf-section">
        <Card>
          <CardHeader title="Monthly spend" sub="Verification charges per month · last 6 months" action={<Legend items={[{ label: 'Above average', tone: 'accent' }, { label: 'Average', tone: 'dash' }]} />} />
          {hasMonthly
            ? <BarChart labels={monthly.map(m => formatMonthLabel(m.month))} values={monthly.map(m => m.spent_cents / 100)} formatValue={v => `$${v >= 1000 ? `${Math.round(v / 100) / 10}k` : Math.round(v * 100) / 100}`} height={250} tooltipSub={i => `${formatMonthLabel(monthly[i].month, true)} · ${monthly[i].verifications.toLocaleString()} checks · ${formatMoney(monthly[i].topup_cents)} topped up`} ariaLabel="Monthly verification spend" />
            : <EmptyState icon={<ReceiptPercentIcon />} title="No spend yet">Charges show up here once live verifications run.</EmptyState>}
        </Card>
        <ApplicantFeeCard wallet={wallet} onSaved={w => { setWallet(w); onWalletChange?.(w) }} />
      </div>

      <Card flush className="sf-section">
        <div className="sf-card-pad" style={{ paddingBottom: 12 }}>
          <CardHeader title="Transactions" sub="Every credit and debit on your wallet" className="" action={<PillButton variant="outline" size="sm" icon={<ArrowPathIcon />} onClick={() => setRefreshKey(k => k + 1)}>Refresh</PillButton>} />
          <Segmented value={txType} options={TX_FILTERS} onChange={v => { setTxType(v); setTxOffset(0) }} ariaLabel="Transaction type" />
        </div>
        {!tx ? <div className="sf-card-pad"><Skeleton h={160} r={18} /></div> : tx.items.length ? (
          <>
            <div className="sf-table-wrap">
              <table className="sf-table">
                <thead><tr><th>Transaction</th><th>Date</th><th>Reference</th><th className="num">Amount</th><th className="num">Balance after</th></tr></thead>
                <tbody>
                  {tx.items.map(t => (
                    <tr key={t.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <TxIcon type={t.type} amount={t.amount_cents} />
                          <div>
                            <div className="strong">{TX_TYPE_LABEL[t.type] ?? t.type}</div>
                            <div className="muted" style={{ fontSize: 12.5, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.description || (t.verification_id ? `Session ${t.verification_id.slice(0, 8)}` : '—')}</div>
                          </div>
                        </div>
                      </td>
                      <td className="muted">{fmtDateTime(t.created_at)}</td>
                      <td className="muted sf-num">{t.reference || '—'}</td>
                      <td className={cx('num', t.amount_cents >= 0 ? 'sf-amt-pos' : 'sf-amt-neg')}>{formatMoney(t.amount_cents, { signed: true })}</td>
                      <td className="num">{formatMoney(t.balance_after_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager offset={txOffset} total={tx.total} limit={PAGE} onChange={setTxOffset} />
          </>
        ) : <EmptyState icon={<BanknotesIcon />} title="No transactions">{txType ? 'Nothing of this type yet.' : 'Top up your wallet to get started.'}</EmptyState>}
      </Card>

      <Card flush className="sf-section">
        <div className="sf-card-pad" style={{ paddingBottom: 6 }}>
          <CardHeader title="Applicant payments" sub="Fees paid by the people you verify" />
        </div>
        {!payments ? <div className="sf-card-pad"><Skeleton h={120} r={18} /></div> : payments.items.length ? (
          <>
            <div className="sf-table-wrap">
              <table className="sf-table">
                <thead><tr><th>Applicant</th><th>Date</th><th>Session</th><th>Status</th><th className="num">Amount</th></tr></thead>
                <tbody>
                  {payments.items.map(p => (
                    <tr key={p.id}>
                      <td><div className="strong">{p.payer_name || '—'}</div><div className="muted" style={{ fontSize: 12.5 }}>{p.payer_email || p.tx_ref}</div></td>
                      <td className="muted">{fmtDateTime(p.created_at)}</td>
                      <td className="muted sf-num">{p.verification_id ? p.verification_id.slice(0, 8) : '—'}</td>
                      <td><StatusTag tone={p.status === 'successful' ? 'ok' : p.status === 'failed' ? 'bad' : 'warn'}>{p.status === 'successful' ? 'Paid' : p.status === 'failed' ? 'Failed' : 'Pending'}</StatusTag></td>
                      <td className="num strong">{formatMoney(p.amount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager offset={payOffset} total={payments.total} limit={PAGE} onChange={setPayOffset} />
          </>
        ) : <EmptyState icon={<UserGroupIcon />} title="No applicant payments yet">{wallet.applicant_fee.enabled ? 'Payments appear as applicants pay the fee.' : 'Switch on the applicant fee to let each person pay for their own check.'}</EmptyState>}
      </Card>

      {showTopup && <TopupModal wallet={wallet} onClose={() => setShowTopup(false)} />}
    </div>
  )
}
